import { useCallback, useMemo, useRef } from 'react';

import { InfiniteData, useQueryClient } from '@tanstack/react-query';

import { getAiBuilderMessagesQueryKey, getAiBuilderSessionQueryKey } from 'api/ai-builder/hooks';
import { ChatSessionResponse, PaginatedMessagesResponse } from 'api/ai-builder/requests';
import { useWebSocket } from 'api/hooks';
import { useInvalidateJourneyActionForms } from 'api/journey-action-forms/hooks';
import { useInvalidateJourneyActions } from 'api/journey-actions/hooks';
import { useInvalidateJourneyForms } from 'api/journey-forms/hooks';
import { useInvalidateJourneys } from 'api/journeys/hooks';

import {
  ChatMessage,
  isReasoningMessageEvent,
  isStatusEvent,
  isToolCallEvent,
  reduceMessagesByWsEvent,
} from './use-ai-builder-chat';

type InfiniteMessagesData = InfiniteData<PaginatedMessagesResponse>;

type UseAIBuilderWebSocketOptions = {
  journeyShortId: string;
  shortId?: string;
};

export const useAIBuilderWebSocket = ({
  journeyShortId,
  shortId,
}: UseAIBuilderWebSocketOptions) => {
  const activeToolCallIdsRef = useRef<Set<string>>(new Set());
  const queryClient = useQueryClient();

  const { invalidateJourneyActions } = useInvalidateJourneyActions();
  const { invalidateJourney, invalidateJourneyAttributes, invalidateJourneyIssues } =
    useInvalidateJourneys();
  const { invalidateJourneyActionFormsList } = useInvalidateJourneyActionForms();
  const { invalidateJourneyForms } = useInvalidateJourneyForms();

  const wsPath = useMemo(() => `ai-builder/${journeyShortId}/`, [journeyShortId]);

  const handleWebSocketMessage = useCallback(
    (event: MessageEvent<string>) => {
      if (!event?.data) return;
      if (!shortId) return;

      const envelope = JSON.parse(event.data);
      if (!envelope || envelope.type !== 'update' || !envelope.payload) return;

      const payload = envelope.payload;

      // Helper to update only the first page (newest messages) while preserving oldestMessageId
      const updateFirstPage = (
        old: InfiniteMessagesData | undefined,
        updater: (page: PaginatedMessagesResponse) => PaginatedMessagesResponse
      ): InfiniteMessagesData | undefined => {
        if (!old || !old.pages.length) {
          return old;
        }

        return {
          ...old,
          pages: old.pages.map((page, index) => {
            if (index === 0) {
              return updater(page);
            }
            return page;
          }),
        };
      };

      const updateSession = (patch: Partial<ChatSessionResponse>) => {
        queryClient.setQueryData(
          getAiBuilderSessionQueryKey(journeyShortId),
          (old: ChatSessionResponse | undefined) => {
            if (!old) return old;
            return { ...old, ...patch };
          }
        );
      };

      queryClient.setQueryData(
        getAiBuilderMessagesQueryKey(journeyShortId),
        (old: InfiniteMessagesData | undefined) => {
          return updateFirstPage(old, (page) => {
            return {
              ...page,
              messages: reduceMessagesByWsEvent(page.messages as ChatMessage[], payload),
            };
          });
        }
      );

      if (isReasoningMessageEvent(payload)) {
        return;
      }

      if (isStatusEvent(payload)) {
        if (['error', 'interrupted', 'completed'].includes(payload.event)) {
          activeToolCallIdsRef.current.clear();
          updateSession({ isGenerating: false });
        }
        return;
      }

      if (isToolCallEvent(payload)) {
        const toolPayload = payload;

        if (toolPayload.event === 'start') {
          activeToolCallIdsRef.current.add(toolPayload.callId);
        } else if (toolPayload.event === 'end') {
          activeToolCallIdsRef.current.delete(toolPayload.callId);
          if (
            [
              'create_action',
              'edit_action',
              'delete_action',
              'move_action',
              'create_trigger',
            ].includes(toolPayload?.toolType ?? '')
          ) {
            invalidateJourneyActions(journeyShortId);
            invalidateJourney(journeyShortId);

            const formTools = [
              'create_combo_send_form_action',
              'edit_combo_send_form_action_config',
              'edit_combo_send_form_action_form',
            ];
            if (formTools.includes(toolPayload?.name ?? '')) {
              invalidateJourneyActionFormsList();
            }

            if (toolPayload?.name === 'create_external_form') {
              invalidateJourneyForms(journeyShortId);
            }
          }

          if (
            ['create_journey_attribute', 'delete_journey_attribute'].includes(
              toolPayload?.toolType ?? ''
            )
          ) {
            invalidateJourneyAttributes(journeyShortId);
            if (toolPayload.toolType === 'delete_journey_attribute') {
              // Deleting an attribute recalculates isConfigured on actions that
              // referenced it, so we need to refetch actions for the warning
              // triangles on nodes to update.
              invalidateJourneyActions(journeyShortId);
              invalidateJourneyIssues(journeyShortId);
            }
          }
        }
      }
    },
    [
      queryClient,
      journeyShortId,
      shortId,
      invalidateJourneyActions,
      invalidateJourney,
      invalidateJourneyAttributes,
      invalidateJourneyIssues,
      invalidateJourneyActionFormsList,
      invalidateJourneyForms,
    ]
  );

  useWebSocket(wsPath, handleWebSocketMessage, {
    enabled: Boolean(wsPath && shortId),
    throttleTime: 0,
  });

  const clearActiveToolCalls = useCallback(() => {
    activeToolCallIdsRef.current.clear();
  }, []);

  return {
    clearActiveToolCalls,
  };
};
